import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, Input, Button, Select, Badge, Icon, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Skeleton, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  FormControl, FormLabel, useDisclosure, IconButton, Tooltip,
  VStack, HStack, Alert, AlertIcon, InputGroup, InputLeftElement,
  Checkbox, Wrap, WrapItem, useBreakpointValue
} from '@chakra-ui/react';
import { Card, Title } from "@tremor/react";
import {
  Plus, Pencil, Trash2, Building2, ShoppingCart, RefreshCw, DollarSign,
  TrendingDown, TrendingUp, Power, Search, Calendar, Upload, Download, FileSpreadsheet
} from 'lucide-react';
import ScrollToTop from "../components/ScrollToTop";
import {
  getDreCustos, getDreCategorias, getDreCanais, createDreCusto, updateDreCusto, deleteDreCusto,
  getDreCustosTemplate, validarImportacaoDreCustos, confirmarImportacaoDreCustos,
  getDreIaOrigens, updateDreIaOrigem,
  getDreComissaoAgentes, updateDreComissaoAgente
} from "../services/api";

const formatMoney = (val) => `R$ ${parseFloat(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const parts = String(dateStr).substring(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
};

const DRECustos = () => {
  const toast = useToast();
  const dataFetchedRef = useRef(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  const [tipo, setTipo] = useState('');
  const [dataInicio, setDataInicio] = useState(primeiroDia.toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState(hoje.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [custos, setCustos] = useState([]);
  const [categorias, setCategorias] = useState({ data: {}, lista: [] });
  const [canaisDisponiveis, setCanaisDisponiveis] = useState([]);
  const [combosCanal, setCombosCanal] = useState([]);

  // Importação em massa (#2238)
  const importDisc = useDisclosure();
  const importFileRef = useRef(null);
  const [impEtapa, setImpEtapa] = useState('inicio'); // inicio | erros | preview | concluido
  const [impArquivoNome, setImpArquivoNome] = useState('');
  const [impErros, setImpErros] = useState([]);
  const [impDespesas, setImpDespesas] = useState([]);
  const [impProcessando, setImpProcessando] = useState(false);
  const [impBaixando, setImpBaixando] = useState(false);
  const [impResumo, setImpResumo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busca, setBusca] = useState('');

  const [iaOrigens, setIaOrigens] = useState([]);
  const [iaPendentes, setIaPendentes] = useState([]);
  const [iaDataInicio, setIaDataInicio] = useState(null);
  const [processamentoMeta, setProcessamentoMeta] = useState(null);
  const iaDisc = useDisclosure();
  const [iaEditando, setIaEditando] = useState(null);
  const [iaSaving, setIaSaving] = useState(false);
  const [iaForm, setIaForm] = useState({ rotulo: '', dre_categoria_custo_id: '', alocacao: 'atribuivel', canais: [] });

  const [comissaoAgentes, setComissaoAgentes] = useState([]);
  const [comissaoPendentes, setComissaoPendentes] = useState([]);
  const [comissaoMeta, setComissaoMeta] = useState(null);
  const [comissaoSalvando, setComissaoSalvando] = useState(null);

  // Form state
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    descricao: '',
    fixo: true,
    valor: '',
    data: hoje.toISOString().slice(0, 10),
    grupoSel: '',
    categoriaSel: '',
    subcategoriaSel: '',
    dreCategoriaId: '',
    dataInicio: '',
    dataFim: '',
    alocacao: 'overhead',
    canaisSel: [],
  });

  const resetForm = () => {
    setEditId(null);
    setForm({
      descricao: '', fixo: true, valor: '',
      data: hoje.toISOString().slice(0, 10),
      grupoSel: '', categoriaSel: '', subcategoriaSel: '', dreCategoriaId: '',
      dataInicio: '', dataFim: '',
      alocacao: 'overhead', canaisSel: [],
    });
  };

  const aplicarComissaoAgentes = (res) => {
    if (!res || !res.data) return;
    setComissaoAgentes(res.data);
    setComissaoPendentes(res.pendentes || []);
    setComissaoMeta({ data_inicio: res.data_inicio_integracao || null });
  };

  const aplicarIaOrigens = (res) => {
    if (!res || !res.data) return;
    setIaOrigens(res.data);
    setIaPendentes(res.pendentes || []);
    setIaDataInicio(res.data_inicio_integracao || null);
  };

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (tipo) params.tipo = tipo;
    if (tipo === 'variavel' || !tipo) {
      params.data_inicio = dataInicio;
      params.data_fim = dataFim;
    }

    const [custosRes, categoriasRes, canaisRes, iaRes, comissaoRes] = await Promise.all([
      getDreCustos(params),
      getDreCategorias(),
      getDreCanais(),
      getDreIaOrigens(),
      getDreComissaoAgentes()
    ]);

    if (custosRes && custosRes.data) setCustos(custosRes.data);
    else if (custosRes === false) toast({ title: 'Erro ao carregar custos', status: 'error', duration: 3000 });
    if (custosRes && custosRes.ia_pendentes) setIaPendentes(custosRes.ia_pendentes);
    if (custosRes && custosRes.processamento) setProcessamentoMeta(custosRes.processamento);
    if (custosRes && custosRes.comissao_pendentes) setComissaoPendentes(custosRes.comissao_pendentes);
    aplicarIaOrigens(iaRes);
    aplicarComissaoAgentes(comissaoRes);
    if (categoriasRes) setCategorias(categoriasRes);
    if (canaisRes && canaisRes.data) {
      setCanaisDisponiveis(canaisRes.data);
      setCombosCanal(canaisRes.combos || []);
    }
    setLoading(false);
  };

  const fetchCustos = async () => {
    setLoading(true);
    const params = {};
    if (tipo) params.tipo = tipo;
    if (tipo === 'variavel' || !tipo) {
      params.data_inicio = dataInicio;
      params.data_fim = dataFim;
    }

    const res = await getDreCustos(params);
    if (res && res.data) setCustos(res.data);
    else if (res === false) toast({ title: 'Erro ao carregar custos', status: 'error', duration: 3000 });
    if (res && res.ia_pendentes) setIaPendentes(res.ia_pendentes);
    if (res && res.processamento) setProcessamentoMeta(res.processamento);
    if (res && res.comissao_pendentes) setComissaoPendentes(res.comissao_pendentes);
    setLoading(false);
  };

  useEffect(() => {
    if (!dataFetchedRef.current) {
      fetchData();
      dataFetchedRef.current = true;
    }
  }, []);

  // ---- Category helpers (estrutura: grupo → { tipo, categorias: { cat: { tipo, subcategorias: [...] } } }) ----
  const categoriaLista = categorias?.lista || [];
  const categoriaTree = categorias?.data || {};

  const categoriaMap = {};
  categoriaLista.forEach(c => { categoriaMap[c.id] = c; });

  // Grupos disponíveis
  const gruposDisponiveis = Object.keys(categoriaTree);

  // Categorias filtradas pelo grupo selecionado
  const categoriasDoGrupo = (() => {
    if (!form.grupoSel || !categoriaTree[form.grupoSel]) return [];
    const grupoData = categoriaTree[form.grupoSel];
    return Object.keys(grupoData.categorias || {});
  })();

  // Folhas do cadastro no grupo + categoria selecionados. Uma folha por caminho
  // completo, então a mesma subcategoria aparece uma vez por item.
  const folhasDaCategoria = (() => {
    if (!form.grupoSel || !form.categoriaSel) return [];
    const grupoData = categoriaTree[form.grupoSel];
    if (!grupoData || !grupoData.categorias) return [];
    const catData = grupoData.categorias[form.categoriaSel];
    if (!catData) return [];
    return Array.isArray(catData.subcategorias) ? catData.subcategorias : [];
  })();

  const subcategoriasOpts = [...new Set(folhasDaCategoria.map(f => f.subcategoria))];

  // Folhas da subcategoria escolhida. Sem item, existe uma folha só e ela é
  // selecionada direto, sem passo extra para quem já usava a tela.
  const folhasDaSubcategoria = folhasDaCategoria.filter(f => f.subcategoria === form.subcategoriaSel);
  const temItens = folhasDaSubcategoria.some(f => f.item);
  const folhaSemItem = folhasDaSubcategoria.find(f => !f.item);

  const escolherSubcategoria = (nome) => {
    const folhas = folhasDaCategoria.filter(f => f.subcategoria === nome);
    const semItem = folhas.find(f => !f.item);
    setForm({ ...form, subcategoriaSel: nome, dreCategoriaId: semItem ? String(semItem.id) : '' });
  };

  // Tipo da categoria selecionada (pelo ID final)
  const tipoCategoriaSelecionada = (() => {
    if (!form.dreCategoriaId) return null;
    const cat = categoriaMap[parseInt(form.dreCategoriaId)];
    return cat?.tipo || null;
  })();

  // ---- Handlers ----
  const handleOpenNew = () => { resetForm(); onOpen(); };

  const handleOpenEdit = (custo) => {
    setEditId(custo.id);
    const catInfo = categoriaMap[custo.dre_categoria_custo_id];
    setForm({
      descricao: custo.descricao,
      fixo: custo.fixo,
      valor: custo.valor,
      data: custo.data || hoje.toISOString().slice(0, 10),
      grupoSel: catInfo?.grupo || custo.grupo || '',
      categoriaSel: catInfo?.categoria || custo.categoria_nome || '',
      subcategoriaSel: catInfo?.subcategoria || custo.subcategoria || '',
      dreCategoriaId: custo.dre_categoria_custo_id ? String(custo.dre_categoria_custo_id) : '',
      dataInicio: custo.data_inicio || '',
      dataFim: custo.data_fim || '',
      alocacao: custo.alocacao || '',
      canaisSel: (custo.canais || []).map(c => c.id),
    });
    onOpen();
  };

  const handleSave = async () => {
    if (!form.descricao.trim()) {
      toast({ title: 'Preencha a descrição', status: 'warning', duration: 3000 });
      return;
    }
    if (!form.valor || parseFloat(form.valor) <= 0) {
      toast({ title: 'Informe um valor válido', status: 'warning', duration: 3000 });
      return;
    }
    if (!form.fixo && !form.data) {
      toast({ title: 'Data é obrigatória para gastos variáveis', status: 'warning', duration: 3000 });
      return;
    }
    if (form.alocacao === 'atribuivel' && form.canaisSel.length === 0) {
      toast({ title: 'Selecione pelo menos um canal para despesa atribuível', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    const payload = {
      descricao: form.descricao.trim(),
      dre_categoria_custo_id: form.dreCategoriaId ? parseInt(form.dreCategoriaId) : null,
      valor: parseFloat(form.valor),
      fixo: form.fixo,
      alocacao: form.alocacao || null,
    };
    if (form.alocacao === 'atribuivel') {
      payload.canais = form.canaisSel;
    }
    if (!form.fixo) {
      payload.data = form.data;
    } else {
      payload.data_inicio = form.dataInicio || null;
      payload.data_fim = form.dataFim || null;
    }

    let res;
    if (editId) res = await updateDreCusto(editId, payload);
    else res = await createDreCusto(payload);

    if (res && (res.status === 200 || res.status === 201)) {
      toast({
        title: editId ? 'Custo atualizado com sucesso' : 'Custo criado com sucesso',
        status: 'success', duration: 3000
      });
      onClose();
      resetForm();
      fetchCustos();
    } else {
      const msg = res?.data?.message || 'Erro ao salvar custo';
      toast({ title: msg, status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  const handleDelete = async (custo) => {
    if (!window.confirm(
      custo.fixo
        ? `Desativar custo fixo "${custo.descricao}"?`
        : `Excluir gasto variável "${custo.descricao}"? Esta ação não pode ser desfeita.`
    )) return;

    const res = await deleteDreCusto(custo.id);
    if (res && res.status === 200) {
      toast({ title: res.data?.message || 'Custo removido', status: 'success', duration: 3000 });
      fetchCustos();
    } else {
      toast({ title: 'Erro ao remover custo', status: 'error', duration: 3000 });
    }
  };

  const handleReativar = async (custo) => {
    const res = await updateDreCusto(custo.id, { ativo: true });
    if (res && res.status === 200) {
      toast({ title: 'Custo reativado com sucesso', status: 'success', duration: 3000 });
      fetchCustos();
    } else {
      toast({ title: 'Erro ao reativar custo', status: 'error', duration: 3000 });
    }
  };

  // ---- Importação em massa (#2238) ----
  const abrirImportacao = () => {
    setImpEtapa('inicio');
    setImpArquivoNome('');
    setImpErros([]);
    setImpDespesas([]);
    setImpResumo(null);
    if (importFileRef.current) importFileRef.current.value = '';
    importDisc.onOpen();
  };

  const baixarTemplate = async () => {
    setImpBaixando(true);
    const blob = await getDreCustosTemplate();
    setImpBaixando(false);
    if (!blob) {
      toast({ title: 'Erro ao baixar o template', status: 'error', duration: 3000 });
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-despesas-dre-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const validarArquivo = async () => {
    const file = importFileRef.current?.files?.[0];
    if (!file) {
      toast({ title: 'Selecione o arquivo .xlsx preenchido', status: 'warning', duration: 3000 });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast({ title: 'Somente arquivos .xlsx são aceitos', status: 'warning', duration: 3000 });
      return;
    }
    setImpProcessando(true);
    setImpArquivoNome(file.name);
    const res = await validarImportacaoDreCustos(file);
    setImpProcessando(false);
    if (!res.ok) {
      toast({ title: res.message, status: 'error', duration: 4000 });
      return;
    }
    if (!res.valido) {
      setImpErros(res.erros || []);
      setImpEtapa('erros');
      return;
    }
    setImpDespesas(res.despesas || []);
    setImpEtapa('preview');
  };

  const editarDespesaImp = (idx, campo, valor) => {
    setImpDespesas(prev => prev.map((d, i) => {
      if (i !== idx) return d;
      const novo = { ...d, [campo]: valor };
      if (campo === 'fixo') {
        if (valor) { novo.data = null; } else { novo.data_inicio = null; novo.data_fim = null; }
      }
      if (campo === 'categoria_caminho') {
        const cat = categoriaLista.find(c => c.label === valor);
        novo.dre_categoria_custo_id = cat ? cat.id : null;
      }
      return novo;
    }));
  };

  const removerDespesaImp = (idx) => {
    setImpDespesas(prev => prev.filter((_, i) => i !== idx));
  };

  const confirmarImportacao = async () => {
    if (impDespesas.length === 0) {
      toast({ title: 'Nenhuma despesa para importar', status: 'warning', duration: 3000 });
      return;
    }
    setImpProcessando(true);
    const payload = impDespesas.map(d => ({
      linha: d.linha,
      descricao: d.descricao,
      tipo: d.fixo ? 'Recorrente' : 'Pontual',
      valor: d.valor,
      data: d.data || null,
      data_inicio: d.data_inicio || null,
      data_fim: d.data_fim || null,
      categoria_caminho: d.categoria_caminho,
      canal_combo: d.canal_combo,
    }));
    const res = await confirmarImportacaoDreCustos(payload);
    setImpProcessando(false);
    if (!res.ok) {
      if (res.erros && res.erros.length > 0) {
        setImpErros(res.erros);
        setImpEtapa('erros');
        toast({ title: 'Importação bloqueada: corrija os itens apontados', status: 'error', duration: 4000 });
      } else {
        toast({ title: res.message || 'Erro ao importar', status: 'error', duration: 4000 });
      }
      return;
    }
    setImpResumo(res);
    setImpEtapa('concluido');
    fetchCustos();
  };

  const somaImportacao = impDespesas.reduce((s, d) => s + parseFloat(d.valor || 0), 0);

  // Modal fullscreen no celular; nas etapas de tabela usa a largura maior no desktop
  const impModalSize = useBreakpointValue({
    base: 'full',
    md: impEtapa === 'inicio' || impEtapa === 'concluido' ? 'lg' : '6xl',
  });

  // ---- Filtros e totais ----
  const filteredCustos = custos.filter(c => {
    if (!busca) return true;
    const term = busca.toLowerCase();
    return (c.descricao || '').toLowerCase().includes(term) ||
      (c.grupo || '').toLowerCase().includes(term) ||
      (c.categoria_nome || '').toLowerCase().includes(term) ||
      (c.subcategoria || '').toLowerCase().includes(term) ||
      (c.item_categoria || '').toLowerCase().includes(term);
  });

  const custosFixos = filteredCustos.filter(c => c.fixo);
  const custosVariaveis = filteredCustos.filter(c => !c.fixo);
  const custosFixosInativos = custosFixos.filter(c => c.ativo === false);

  // Custos automáticos (#2384 IA, #2383 processamento) são gerados um
  // lançamento por origem por dia — algumas centenas por mês. Listados linha a
  // linha afogariam os lançamentos manuais, então saem da tabela de gastos
  // variáveis e aparecem agrupados num bloco próprio por integração. Os totais
  // do topo continuam contando todos eles, porque no DRE são despesa como
  // qualquer outra.
  const custosVariaveisManuais = custosVariaveis.filter(c => !c.automatico);
  const custosIa = custosVariaveis.filter(c => c.origem_tipo === 'ia');
  const custosProcessamento = custosVariaveis.filter(c => c.origem_tipo === 'processamento');
  const custosComissao = custosVariaveis.filter(c => c.origem_tipo === 'comissao');
  // Gasto de anúncios lido de cada plataforma (#2610). Uma linha por plataforma e
  // canal: o Mercado Livre Ads de cada conta vai para o canal da própria conta.
  const ROTULO_ANUNCIOS = { gads: 'Google Ads', meta: 'Meta Ads', mlads: 'Mercado Livre Ads' };
  const linhasAnuncios = Object.values(
    custosVariaveis.filter(c => ROTULO_ANUNCIOS[c.origem_tipo]).reduce((acc, c) => {
      const chave = `${c.origem_tipo}:${(c.canais || []).map(canal => canal.id).join(',')}`;
      if (!acc[chave]) {
        acc[chave] = {
          chave,
          rotulo: ROTULO_ANUNCIOS[c.origem_tipo],
          caminho: [c.grupo, c.categoria_nome, c.subcategoria, c.item_categoria].filter(Boolean).join(' > '),
          canais: c.canais || [],
          lancamentos: 0,
          valor: 0,
        };
      }
      acc[chave].lancamentos += (c.lancamentos || []).length || 1;
      acc[chave].valor += parseFloat(c.valor || 0);
      return acc;
    }, {})
  ).sort((a, b) => b.valor - a.valor);
  const totalAnuncios = linhasAnuncios.reduce((s, o) => s + o.valor, 0);

  const comissaoDoPeriodo = custosComissao.reduce((acc, c) => {
    const chave = String(c.origem_chave_tipo || 'desconhecido');
    if (!acc[chave]) acc[chave] = { valor: 0, lancamentos: 0, canais: {} };
    acc[chave].valor += parseFloat(c.valor || 0);
    acc[chave].lancamentos += 1;
    (c.canais || []).forEach((canal) => { acc[chave].canais[canal.id] = canal; });
    return acc;
  }, {});

  const linhasComissao = comissaoAgentes
    .map((a) => {
      const doPeriodo = comissaoDoPeriodo[String(a.user_id)];
      return {
        ...a,
        valor: doPeriodo ? doPeriodo.valor : 0,
        lancamentos: doPeriodo ? doPeriodo.lancamentos : 0,
        canais: doPeriodo ? Object.values(doPeriodo.canais) : [],
      };
    })
    .sort((a, b) => {
      if (!!a.pendente_classificacao !== !!b.pendente_classificacao) return a.pendente_classificacao ? -1 : 1;
      return b.valor - a.valor;
    });

  const totalComissao = linhasComissao.reduce((s, o) => s + o.valor, 0);

  const custosIaPorOrigem = Object.values(
    custosIa.reduce((acc, c) => {
      const chave = c.origem_ia || 'desconhecida';
      if (!acc[chave]) {
        acc[chave] = {
          origem: chave,
          // "IA - Resposta do bot (US$ ... x ...)" -> "Resposta do bot"
          rotulo: (c.descricao || '').replace(/^IA - /, '').replace(/\s*\(US\$.*$/, '') || chave,
          grupo: c.grupo,
          categoria_nome: c.categoria_nome,
          subcategoria: c.subcategoria,
          item_categoria: c.item_categoria,
          tipo_categoria: c.tipo_categoria,
          canais: c.canais,
          lancamentos: 0,
          valor: 0,
        };
      }
      acc[chave].lancamentos += 1;
      acc[chave].valor += parseFloat(c.valor || 0);
      return acc;
    }, {})
  ).sort((a, b) => b.valor - a.valor);

  const totalIa = custosIaPorOrigem.reduce((s, o) => s + o.valor, 0);

  // Processamento (#2383): dois tipos fixos, um lançamento por dia cada.
  // Diferente da IA, aqui não existe origem desconhecida — a classificação
  // contábil dos dois tipos é a do ticket e não se reclassifica em tela.
  const ROTULO_PROCESSAMENTO = {
    despacho: { titulo: 'Etiquetas expedidas', detalhe: 'Faixas regressivas por semana: 1–100 × R$ 10,00 · 101–150 × R$ 5,00 · 151+ × R$ 1,00' },
    devolucoes: { titulo: 'Recebimento de devoluções', detalhe: 'R$ 10,00 por etiqueta recebida' },
  };

  const linhasProcessamento = Object.values(
    custosProcessamento.reduce((acc, c) => {
      const chave = c.origem_chave_tipo || 'desconhecida';
      if (!acc[chave]) {
        acc[chave] = {
          chave,
          rotulo: ROTULO_PROCESSAMENTO[chave]?.titulo
            // fallback: "Remuneracao do processador (83 etiq., semana ...)" -> rótulo
            || (c.descricao || '').replace(/\s*\([^()]*\)\s*$/, '')
            || chave,
          detalhe: ROTULO_PROCESSAMENTO[chave]?.detalhe || '',
          grupo: c.grupo,
          categoria_nome: c.categoria_nome,
          subcategoria: c.subcategoria,
          item_categoria: c.item_categoria,
          canais: c.canais,
          lancamentos: 0,
          valor: 0,
          previa: false,
        };
      }
      acc[chave].lancamentos += 1;
      acc[chave].valor += parseFloat(c.valor || 0);
      // Semana ainda aberta: as faixas são regressivas, então o valor do
      // período ainda muda conforme o volume da semana cresce.
      if ((c.descricao || '').includes('prévia')) acc[chave].previa = true;
      return acc;
    }, {})
  ).sort((a, b) => b.valor - a.valor);

  const totalProcessamento = linhasProcessamento.reduce((s, o) => s + o.valor, 0);

  const valorIaDoPeriodo = custosIaPorOrigem.reduce((acc, o) => {
    acc[o.origem] = o;
    return acc;
  }, {});

  const linhasIa = iaOrigens
    .map((o) => {
      const doPeriodo = valorIaDoPeriodo[o.origem];
      return {
        ...o,
        valor: doPeriodo ? doPeriodo.valor : 0,
        lancamentos: doPeriodo ? doPeriodo.lancamentos : 0,
      };
    })
    .sort((a, b) => {
      if (!!a.pendente_classificacao !== !!b.pendente_classificacao) return a.pendente_classificacao ? -1 : 1;
      return b.valor - a.valor;
    });

  const totalFixosDespesas = custosFixos
    .filter(c => c.ativo !== false && c.tipo_categoria !== 'receita')
    .reduce((s, c) => s + parseFloat(c.valor || 0), 0);
  const totalFixosReceitas = custosFixos
    .filter(c => c.ativo !== false && c.tipo_categoria === 'receita')
    .reduce((s, c) => s + parseFloat(c.valor || 0), 0);
  const totalVariaveisDespesas = custosVariaveis
    .filter(c => c.tipo_categoria !== 'receita')
    .reduce((s, c) => s + parseFloat(c.valor || 0), 0);
  const totalVariaveisReceitas = custosVariaveis
    .filter(c => c.tipo_categoria === 'receita')
    .reduce((s, c) => s + parseFloat(c.valor || 0), 0);
  const totalLiquido = (totalFixosDespesas + totalVariaveisDespesas) - (totalFixosReceitas + totalVariaveisReceitas);

  // ---- Render helpers ----
  const renderTipoBadge = (tipo_cat) => {
    if (tipo_cat === 'receita') return <Badge colorScheme="green" fontSize="2xs" borderRadius="full" px={2}>Receita</Badge>;
    if (tipo_cat === 'despesa') return <Badge colorScheme="red" fontSize="2xs" borderRadius="full" px={2}>Despesa</Badge>;
    return <Badge colorScheme="gray" fontSize="2xs" borderRadius="full" px={2}>Sem cat.</Badge>;
  };

  const renderAlocacaoBadge = (custo) => {
    if (custo.alocacao === 'overhead') {
      return <Badge colorScheme="gray" fontSize="2xs" borderRadius="full" px={2}>Overhead</Badge>;
    }
    if (custo.alocacao === 'atribuivel') {
      const nomes = (custo.canais || []).map(c => c.nome).join(', ');
      return (
        <Tooltip label={nomes || 'Sem canais vinculados'}>
          <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2}>
            Atribuível ({(custo.canais || []).length})
          </Badge>
        </Tooltip>
      );
    }
    return <Badge colorScheme="yellow" fontSize="2xs" borderRadius="full" px={2}>Não classificado</Badge>;
  };

  const renderCategoriaInfo = (custo) => {
    if (!custo.grupo && !custo.subcategoria) return <Text fontSize="xs" color="gray.400">Sem categoria</Text>;
    return (
      <VStack spacing={0} align="start">
        <Text fontSize="2xs" color="gray.400">{custo.grupo} › {custo.categoria_nome}</Text>
        <Text fontSize="xs" fontWeight="medium" color="gray.700">
          {custo.subcategoria}{custo.item_categoria ? ` › ${custo.item_categoria}` : ''}
        </Text>
      </VStack>
    );
  };

  const renderCaminhoCategoria = (label) => {
    if (!label) return <Text fontSize="xs" color="gray.400">Sem categoria</Text>;
    const partes = String(label).split('>').map(p => p.trim());
    return (
      <VStack spacing={0} align="start">
        <Text fontSize="2xs" color="gray.400">{partes.slice(0, 2).join(' › ')}</Text>
        <Text fontSize="xs" fontWeight="medium" color="gray.700">{partes.slice(2).join(' › ')}</Text>
      </VStack>
    );
  };

  const handleOpenEditIa = (o) => {
    setIaEditando(o);
    setIaForm({
      rotulo: o.rotulo || '',
      dre_categoria_custo_id: o.dre_categoria_custo_id ? String(o.dre_categoria_custo_id) : '',
      alocacao: o.alocacao || 'atribuivel',
      canais: (o.canais || []).map(c => c.id),
    });
    iaDisc.onOpen();
  };

  const toggleCanalIa = (id) => {
    setIaForm((f) => ({
      ...f,
      canais: f.canais.includes(id) ? f.canais.filter(c => c !== id) : [...f.canais, id],
    }));
  };

  const handleSaveIa = async () => {
    if (!iaForm.dre_categoria_custo_id) {
      toast({ title: 'Escolha a classificação contábil', status: 'warning', duration: 3000 });
      return;
    }
    if (iaForm.alocacao === 'atribuivel' && iaForm.canais.length === 0) {
      toast({ title: 'Origem atribuível precisa de pelo menos um canal', status: 'warning', duration: 3000 });
      return;
    }

    setIaSaving(true);
    const res = await updateDreIaOrigem(iaEditando.id, {
      rotulo: iaForm.rotulo,
      dre_categoria_custo_id: Number(iaForm.dre_categoria_custo_id),
      alocacao: iaForm.alocacao,
      canais: iaForm.canais,
    });
    setIaSaving(false);

    if (res && res.status >= 200 && res.status < 300) {
      toast({ title: 'Origem reclassificada', status: 'success', duration: 3000 });
      iaDisc.onClose();
      aplicarIaOrigens(await getDreIaOrigens());
      fetchCustos();
    } else {
      toast({ title: res?.data?.message || 'Erro ao reclassificar origem', status: 'error', duration: 4000 });
    }
  };

  const alternarComissaoAgente = async (agente) => {
    setComissaoSalvando(agente.id);
    const res = await updateDreComissaoAgente(agente.id, { ativo: !agente.ativo });
    setComissaoSalvando(null);

    if (res && res.status >= 200 && res.status < 300) {
      toast({
        title: agente.ativo
          ? `Comissão de ${agente.rotulo} não será mais lançada`
          : `Comissão de ${agente.rotulo} passa a ser lançada`,
        status: 'success',
        duration: 3000,
      });
      aplicarComissaoAgentes(await getDreComissaoAgentes());
      fetchCustos();
    } else {
      toast({ title: res?.data?.message || 'Erro ao atualizar o agente', status: 'error', duration: 4000 });
    }
  };

  return (
    <Box className="min-h-screen bg-gray-50" pt="60px">
      <ScrollToTop />
      <Box maxW="1400px" mx="auto" px={{ base: 3, md: 6 }} py={6}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">Custos e Receitas</Text>
            <Text fontSize="sm" color="gray.500">Gerenciamento de custos fixos, gastos variáveis e receitas</Text>
          </Box>
          <HStack spacing={2} wrap="wrap">
            <Button size="sm" variant="outline" colorScheme="green" leftIcon={<Upload size={16} />} onClick={abrirImportacao}>
              Importar planilha
            </Button>
            <Button size="sm" colorScheme="blue" leftIcon={<Plus size={16} />} onClick={handleOpenNew}>
              Novo Lançamento
            </Button>
          </HStack>
        </Flex>

        {/* Origens de IA sem classificação definitiva (#2384). O custo já entra
            no DRE em "Tokens IA > Outros" — o alerta é para que alguém defina a
            classificação certa, não porque algo tenha sido perdido. */}
        {iaPendentes.length > 0 && (
          <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">
                {iaPendentes.length === 1
                  ? '1 origem de IA está sem classificação definitiva'
                  : `${iaPendentes.length} origens de IA estão sem classificação definitiva`}
              </Text>
              <Text fontSize="xs" color="gray.600">
                Lançadas em <b>Tokens IA &gt; Outros</b>, rateadas entre todos os canais:{' '}
                {iaPendentes.map(o => o.rotulo || o.origem).join(', ')}. Classifique nas linhas
                destacadas do bloco <b>Custos de IA (automático)</b>, abaixo.
              </Text>
            </Box>
          </Alert>
        )}

        {/* Agentes com venda atribuída que ainda não lançam comissão (#2385).
            Nada foi perdido: a comissão deles simplesmente não entra no DRE
            enquanto alguém não decidir se aquela pessoa recebe comissão. */}
        {comissaoPendentes.length > 0 && (
          <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">
                {comissaoPendentes.length === 1
                  ? '1 pessoa tem venda atribuída e não lança comissão'
                  : `${comissaoPendentes.length} pessoas têm venda atribuída e não lançam comissão`}
              </Text>
              <Text fontSize="xs" color="gray.600">
                {comissaoPendentes.map(a => a.rotulo).join(', ')}. Só quem está cadastrado como
                vendedor entra automático. Ligue no bloco <b>Comissões (automático)</b>, abaixo,
                se essa pessoa também recebe comissão.
              </Text>
            </Box>
          </Alert>
        )}

        {/* Filtros */}
        <Card className="ring-0 mb-6">
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Tipo</Text>
              <Select size="sm" value={tipo} onChange={(e) => setTipo(e.target.value)} bg="white" borderRadius="md" placeholder="Todos">
                <option value="fixo">Fixo</option>
                <option value="variavel">Variável</option>
              </Select>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Início</Text>
              <Input type="date" size="sm" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Fim</Text>
              <Input type="date" size="sm" value={dataFim} onChange={(e) => setDataFim(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box flex={1} minW="180px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Buscar</Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none"><Search size={14} color="gray" /></InputLeftElement>
                <Input placeholder="Descrição, categoria..." value={busca} onChange={(e) => setBusca(e.target.value)} bg="white" borderRadius="md" />
              </InputGroup>
            </Box>
            <Button size="sm" colorScheme="blue" leftIcon={<RefreshCw size={14} />} onClick={fetchCustos} isLoading={loading} px={6}>
              Filtrar
            </Button>
          </Flex>
        </Card>

        {/* Resumo */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="red.50">
                <Icon as={TrendingDown} boxSize={5} color="red.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Despesas Fixas</Text>
                {loading ? <Skeleton h="24px" w="100px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="red.600">{formatMoney(totalFixosDespesas)}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="green.50">
                <Icon as={TrendingUp} boxSize={5} color="green.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Receitas Fixas</Text>
                {loading ? <Skeleton h="24px" w="100px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="green.600">{formatMoney(totalFixosReceitas)}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="orange.50">
                <Icon as={ShoppingCart} boxSize={5} color="orange.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Gastos Variáveis</Text>
                {loading ? <Skeleton h="24px" w="100px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="orange.600">{formatMoney(totalVariaveisDespesas + totalVariaveisReceitas)}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="purple.50">
                <Icon as={DollarSign} boxSize={5} color="purple.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Custo Líquido</Text>
                {loading ? <Skeleton h="24px" w="100px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="purple.600">{formatMoney(totalLiquido)}</Text>
                )}
              </Box>
            </Flex>
          </Card>
        </SimpleGrid>

        {/* Custos Fixos */}
        {(tipo === '' || tipo === 'fixo') && (
          <Card className="ring-0 mb-6">
            <Flex justify="space-between" align="center" mb={4}>
              <Flex align="center" gap={2}>
                <Title>Custos Fixos</Title>
                <Badge colorScheme="blue" borderRadius="full" px={3}>
                  {custosFixos.filter(c => c.ativo !== false).length} ativos
                </Badge>
                {custosFixosInativos.length > 0 && (
                  <Badge colorScheme="gray" borderRadius="full" px={3}>{custosFixosInativos.length} inativos</Badge>
                )}
              </Flex>
            </Flex>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="xs">Descrição</Th>
                    <Th fontSize="xs">Categoria</Th>
                    <Th fontSize="xs">Tipo</Th>
                    <Th fontSize="xs">Alocação</Th>
                    <Th fontSize="xs">Vigência</Th>
                    <Th fontSize="xs" isNumeric>Valor Mensal</Th>
                    <Th fontSize="xs" w="90px">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <Tr key={i}>{[...Array(7)].map((_, j) => <Td key={j}><Skeleton h="16px" w="80px" /></Td>)}</Tr>
                    ))
                  ) : custosFixos.length === 0 ? (
                    <Tr><Td colSpan={7}><Text color="gray.400" textAlign="center" py={6}>Nenhum custo fixo cadastrado</Text></Td></Tr>
                  ) : (
                    custosFixos.map((c) => (
                      <Tr key={c.id} _hover={{ bg: 'gray.50' }} opacity={c.ativo === false ? 0.5 : 1}>
                        <Td>
                          <Flex align="center" gap={2}>
                            <Text fontSize="sm" fontWeight="medium" color="gray.800">{c.descricao}</Text>
                            {c.ativo === false && <Badge colorScheme="red" fontSize="2xs" borderRadius="full">Inativo</Badge>}
                          </Flex>
                        </Td>
                        <Td>{renderCategoriaInfo(c)}</Td>
                        <Td>{renderTipoBadge(c.tipo_categoria)}</Td>
                        <Td>{renderAlocacaoBadge(c)}</Td>
                        <Td>
                          {!c.data_inicio && !c.data_fim ? (
                            <Text fontSize="xs" color="gray.400">Permanente</Text>
                          ) : (
                            <VStack spacing={0} align="start">
                              <Text fontSize="2xs" color="gray.500">
                                {c.data_inicio ? formatDate(c.data_inicio) : 'Início indef.'} — {c.data_fim ? formatDate(c.data_fim) : 'Sem fim'}
                              </Text>
                            </VStack>
                          )}
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" fontWeight="bold" color={c.tipo_categoria === 'receita' ? 'green.600' : 'red.600'}>
                            {c.tipo_categoria === 'receita' ? '+' : '-'} {formatMoney(c.valor)}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Editar">
                              <IconButton icon={<Pencil size={14} />} size="xs" variant="ghost" colorScheme="blue" onClick={() => handleOpenEdit(c)} />
                            </Tooltip>
                            {c.ativo === false ? (
                              <Tooltip label="Reativar">
                                <IconButton icon={<Power size={14} />} size="xs" variant="ghost" colorScheme="green" onClick={() => handleReativar(c)} />
                              </Tooltip>
                            ) : (
                              <Tooltip label="Desativar">
                                <IconButton icon={<Trash2 size={14} />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(c)} />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Gastos Variáveis */}
        {(tipo === '' || tipo === 'variavel') && (
          <Card className="ring-0">
            <Flex justify="space-between" align="center" mb={4}>
              <Flex align="center" gap={2}>
                <Title>Gastos Variáveis</Title>
                <Badge colorScheme="orange" borderRadius="full" px={3}>{custosVariaveisManuais.length} itens</Badge>
              </Flex>
            </Flex>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="xs">Descrição</Th>
                    <Th fontSize="xs">Categoria</Th>
                    <Th fontSize="xs">Tipo</Th>
                    <Th fontSize="xs">Alocação</Th>
                    <Th fontSize="xs">Data</Th>
                    <Th fontSize="xs" isNumeric>Valor</Th>
                    <Th fontSize="xs" w="90px">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <Tr key={i}>{[...Array(7)].map((_, j) => <Td key={j}><Skeleton h="16px" w="80px" /></Td>)}</Tr>
                    ))
                  ) : custosVariaveisManuais.length === 0 ? (
                    <Tr><Td colSpan={7}><Text color="gray.400" textAlign="center" py={6}>Nenhum gasto variável no período</Text></Td></Tr>
                  ) : (
                    custosVariaveisManuais.map((c) => (
                      <Tr key={c.id} _hover={{ bg: 'gray.50' }}>
                        <Td fontSize="sm" fontWeight="medium" color="gray.800">{c.descricao}</Td>
                        <Td>{renderCategoriaInfo(c)}</Td>
                        <Td>{renderTipoBadge(c.tipo_categoria)}</Td>
                        <Td>{renderAlocacaoBadge(c)}</Td>
                        <Td fontSize="sm" color="gray.600">{formatDate(c.data)}</Td>
                        <Td isNumeric>
                          <Text fontSize="sm" fontWeight="bold" color={c.tipo_categoria === 'receita' ? 'green.600' : 'orange.600'}>
                            {c.tipo_categoria === 'receita' ? '+' : '-'} {formatMoney(c.valor)}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Editar">
                              <IconButton icon={<Pencil size={14} />} size="xs" variant="ghost" colorScheme="blue" onClick={() => handleOpenEdit(c)} />
                            </Tooltip>
                            <Tooltip label="Excluir">
                              <IconButton icon={<Trash2 size={14} />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(c)} />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Anúncios: gasto diário lido de cada plataforma (#2610) */}
        {(tipo === '' || tipo === 'variavel') && linhasAnuncios.length > 0 && (
          <Card className="ring-0">
            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
              <Title>Anúncios (automático)</Title>
              <Text fontSize="sm" fontWeight="bold" color="orange.600">
                Total no período: {formatMoney(totalAnuncios)}
              </Text>
            </Flex>
            <Text fontSize="xs" color="gray.500" mb={3}>
              O gasto de cada dia vem direto da plataforma de anúncios (Google Ads, Meta Ads e
              Mercado Livre Ads, este no canal da própria conta) e atualiza sozinho de hora em
              hora. Não se lança à mão.
            </Text>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="xs">Plataforma</Th>
                    <Th fontSize="xs">Categoria</Th>
                    <Th fontSize="xs">Canais</Th>
                    <Th fontSize="xs" isNumeric>Dias</Th>
                    <Th fontSize="xs" isNumeric>Valor no período</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {linhasAnuncios.map((o) => (
                    <Tr key={o.chave} _hover={{ bg: 'gray.50' }}>
                      <Td><Text fontSize="sm" fontWeight="medium" color="gray.800">{o.rotulo}</Text></Td>
                      <Td>{renderCaminhoCategoria(o.caminho)}</Td>
                      <Td>
                        <Wrap spacing={1}>
                          {o.canais.map((canal) => (
                            <WrapItem key={canal.id}>
                              <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">{canal.nome}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                      <Td isNumeric><Text fontSize="sm">{o.lancamentos}</Text></Td>
                      <Td isNumeric><Text fontSize="sm" fontWeight="semibold">{formatMoney(o.valor)}</Text></Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Processamento — integração automática (#2383) */}
        {(tipo === '' || tipo === 'variavel') && linhasProcessamento.length > 0 && (
          <Card className="ring-0">
            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
              <Flex align="center" gap={2} wrap="wrap" minW={0}>
                <Title>Processamento (automático)</Title>
                <Badge colorScheme="teal" borderRadius="full" px={3}>
                  {linhasProcessamento.length} {linhasProcessamento.length === 1 ? 'tipo' : 'tipos'}
                </Badge>
                {processamentoMeta?.data_inicio && (
                  <Badge colorScheme="gray" borderRadius="full" px={3} whiteSpace="normal">
                    Integração a partir de {formatDate(processamentoMeta.data_inicio)}
                  </Badge>
                )}
                {processamentoMeta?.teto_devolucoes != null && (
                  <Badge colorScheme="purple" borderRadius="full" px={3} whiteSpace="normal">
                    Teto de devoluções: {formatMoney(processamentoMeta.teto_devolucoes)}/semana
                  </Badge>
                )}
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="orange.600">
                Total no período: {formatMoney(totalProcessamento)}
              </Text>
            </Flex>

            <Text fontSize="xs" color="gray.500" mb={3}>
              Vem da tela de Monitoramento de Despacho. A remuneração é apurada por semana
              (domingo a sábado) e repartida entre os dias na proporção do volume de cada dia, para
              que qualquer recorte de datas — inclusive a semana que atravessa a virada do mês —
              fique exato. O rateio entre os canais é pelo <b>volume de etiquetas</b>, e não pela
              receita. O valor atualiza sozinho e não se lança à mão.
            </Text>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="xs">Tipo</Th>
                    <Th fontSize="xs">Categoria</Th>
                    <Th fontSize="xs">Canais</Th>
                    <Th fontSize="xs" isNumeric>Lançamentos</Th>
                    <Th fontSize="xs" isNumeric>Valor no período</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {linhasProcessamento.map((o) => (
                    <Tr key={o.chave} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <VStack spacing={0} align="start">
                          <Flex align="center" gap={2} wrap="wrap">
                            <Text fontSize="sm" fontWeight="medium" color="gray.800">{o.rotulo}</Text>
                            {o.previa && (
                              <Tooltip label="A semana corrente ainda está aberta. Como as faixas são regressivas, o custo médio por etiqueta cai conforme o volume da semana cresce — o valor só estabiliza no sábado.">
                                <Badge colorScheme="yellow" fontSize="2xs" borderRadius="full">Prévia</Badge>
                              </Tooltip>
                            )}
                          </Flex>
                          {o.detalhe && (
                            <Text fontSize="2xs" color="gray.400">{o.detalhe}</Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        {renderCaminhoCategoria(
                          [o.grupo, o.categoria_nome, o.subcategoria, o.item_categoria]
                            .filter(Boolean).join(' > ')
                        )}
                      </Td>
                      <Td>
                        <Wrap spacing={1}>
                          {(o.canais || []).length === 0 ? (
                            <Text fontSize="xs" color="gray.400">Sem canais</Text>
                          ) : (
                            o.canais.map((canal) => (
                              <WrapItem key={canal.id}>
                                <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">{canal.nome}</Badge>
                              </WrapItem>
                            ))
                          )}
                        </Wrap>
                      </Td>
                      <Td isNumeric><Text fontSize="xs" color="gray.500">{o.lancamentos}</Text></Td>
                      <Td isNumeric>
                        <Text fontSize="sm" fontWeight="bold" color="orange.600">
                          - {formatMoney(o.valor)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Comissões da equipe comercial — integração automática (#2385) */}
        {(tipo === '' || tipo === 'variavel') && linhasComissao.length > 0 && (
          <Card className="ring-0">
            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
              <Flex align="center" gap={2} wrap="wrap" minW={0}>
                <Title>Comissões (automático)</Title>
                <Badge colorScheme="teal" borderRadius="full" px={3}>
                  {linhasComissao.length} {linhasComissao.length === 1 ? 'agente' : 'agentes'}
                </Badge>
                {comissaoMeta?.data_inicio && (
                  <Badge colorScheme="gray" borderRadius="full" px={3} whiteSpace="normal">
                    Integração a partir de {formatDate(comissaoMeta.data_inicio)}
                  </Badge>
                )}
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="orange.600">
                Total no período: {formatMoney(totalComissao)}
              </Text>
            </Flex>

            <Text fontSize="xs" color="gray.500" mb={3}>
              Vem da tela de Vendas do Vendedor, com a mesma regra dela (comissão registrada
              prevalece; sem registro, 3% no site e nas vendas manuais e 1,5% no Mercado Livre).
              Cada comissão é lançada no <b>canal da venda que a originou</b>, sem rateio, no dia
              da venda. Venda cancelada ou devolvida deixa de contar sozinha, então o total aqui
              fica ligeiramente abaixo do da tela. O valor atualiza sozinho e não se lança à mão.
            </Text>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="xs">Agente</Th>
                    <Th fontSize="xs">Categoria</Th>
                    <Th fontSize="xs">Canais</Th>
                    <Th fontSize="xs" isNumeric>Lançamentos</Th>
                    <Th fontSize="xs" isNumeric>Valor no período</Th>
                    <Th fontSize="xs" width="60px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {linhasComissao.map((o) => (
                    <Tr key={o.id} _hover={{ bg: 'gray.50' }} bg={o.pendente_classificacao ? 'blue.50' : undefined}>
                      <Td>
                        <VStack spacing={0} align="start">
                          <Flex align="center" gap={2} wrap="wrap">
                            <Text fontSize="sm" fontWeight="medium" color="gray.800">{o.rotulo}</Text>
                            {!o.ativo && (
                              <Tooltip label="Esta pessoa tem venda atribuída, mas a comissão dela não entra no DRE. Ligue se ela também recebe comissão.">
                                <Badge colorScheme="gray" fontSize="2xs" borderRadius="full">Não lança</Badge>
                              </Tooltip>
                            )}
                          </Flex>
                          <Text fontSize="2xs" color="gray.400">Usuário #{o.user_id}</Text>
                        </VStack>
                      </Td>
                      <Td>
                        {o.categoria_label
                          ? renderCaminhoCategoria(o.categoria_label)
                          : <Text fontSize="xs" color="gray.400">Sem categoria</Text>}
                      </Td>
                      <Td>
                        <Wrap spacing={1}>
                          {(o.canais || []).length === 0 ? (
                            <Text fontSize="xs" color="gray.400">Sem lançamento no período</Text>
                          ) : (
                            o.canais.map((canal) => (
                              <WrapItem key={canal.id}>
                                <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">{canal.nome}</Badge>
                              </WrapItem>
                            ))
                          )}
                        </Wrap>
                      </Td>
                      <Td isNumeric><Text fontSize="xs" color="gray.500">{o.lancamentos}</Text></Td>
                      <Td isNumeric>
                        <Text fontSize="sm" fontWeight="bold" color={o.valor > 0 ? 'orange.600' : 'gray.400'}>
                          {o.valor > 0 ? `- ${formatMoney(o.valor)}` : formatMoney(0)}
                        </Text>
                      </Td>
                      <Td>
                        <Tooltip label={o.ativo ? 'Parar de lançar a comissão deste agente no DRE' : 'Passar a lançar a comissão deste agente no DRE'}>
                          <IconButton
                            aria-label={o.ativo ? 'Desligar lançamento' : 'Ligar lançamento'}
                            icon={<Power size={14} />}
                            size="xs"
                            variant="ghost"
                            colorScheme={o.ativo ? 'green' : 'gray'}
                            isLoading={comissaoSalvando === o.id}
                            onClick={() => alternarComissaoAgente(o)}
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Custos de IA — integração automática (#2384) */}
        {(tipo === '' || tipo === 'variavel') && linhasIa.length > 0 && (
          <Card className="ring-0">
            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
              <Flex align="center" gap={2} wrap="wrap" minW={0}>
                <Title>Custos de IA (automático)</Title>
                <Badge colorScheme="purple" borderRadius="full" px={3}>
                  {linhasIa.length} origens
                </Badge>
                {iaDataInicio && (
                  <Badge colorScheme="gray" borderRadius="full" px={3} whiteSpace="normal">
                    Integração a partir de {formatDate(iaDataInicio)}
                  </Badge>
                )}
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="orange.600">
                Total no período: {formatMoney(totalIa)}
              </Text>
            </Flex>

            <Text fontSize="xs" color="gray.500" mb={3}>
              Vem da tela de Custos IA, convertido de US$ pela cotação média (PTAX) do mês de
              competência. O valor atualiza sozinho e não se lança à mão. No lápis você muda a
              classificação contábil e os canais, e salvar já reaponta os lançamentos existentes.
              Origem nova entra sozinha em Tokens IA &gt; Outros e nunca deixa de ser lançada.
            </Text>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="xs">Origem</Th>
                    <Th fontSize="xs">Categoria</Th>
                    <Th fontSize="xs">Canais</Th>
                    <Th fontSize="xs" isNumeric>Lançamentos</Th>
                    <Th fontSize="xs" isNumeric>Valor no período</Th>
                    <Th fontSize="xs" w="60px">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {linhasIa.map((o) => (
                    <Tr
                      key={o.origem}
                      _hover={{ bg: 'gray.50' }}
                      bg={o.pendente_classificacao ? 'orange.50' : undefined}
                      opacity={o.valor === 0 ? 0.6 : 1}
                    >
                      <Td>
                        <VStack spacing={0} align="start">
                          <Flex align="center" gap={2}>
                            <Text fontSize="sm" fontWeight="medium" color="gray.800">{o.rotulo}</Text>
                            {o.pendente_classificacao && (
                              <Badge colorScheme="orange" fontSize="2xs" borderRadius="full">Classificar</Badge>
                            )}
                          </Flex>
                          <Text fontSize="2xs" color="gray.400" fontFamily="mono">{o.origem}</Text>
                        </VStack>
                      </Td>
                      <Td>{renderCaminhoCategoria(o.categoria_label)}</Td>
                      <Td>
                        <Wrap spacing={1}>
                          {(o.canais || []).length === 0 ? (
                            <Text fontSize="xs" color="gray.400">Overhead</Text>
                          ) : (
                            o.canais.map((canal) => (
                              <WrapItem key={canal.id}>
                                <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">{canal.nome}</Badge>
                              </WrapItem>
                            ))
                          )}
                        </Wrap>
                      </Td>
                      <Td isNumeric><Text fontSize="xs" color="gray.500">{o.lancamentos}</Text></Td>
                      <Td isNumeric>
                        {o.valor === 0 ? (
                          <Text fontSize="xs" color="gray.400">sem gasto no período</Text>
                        ) : (
                          <Text fontSize="sm" fontWeight="bold" color="orange.600">
                            - {formatMoney(o.valor)}
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Tooltip label="Reclassificar origem">
                          <IconButton
                            icon={<Pencil size={14} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleOpenEditIa(o)}
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Box>

      {/* Modal Criar/Editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" pb={2}>
            <Flex align="center" gap={2}>
              <Text>{editId ? 'Editar Lançamento' : 'Novo Lançamento'}</Text>
              {tipoCategoriaSelecionada && (
                <Badge colorScheme={tipoCategoriaSelecionada === 'receita' ? 'green' : 'red'} fontSize="xs" borderRadius="full" px={2}>
                  {tipoCategoriaSelecionada === 'receita' ? 'Receita' : 'Despesa'}
                </Badge>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {/* Tipo: Fixo ou Variável */}
              <FormControl>
                <Flex gap={2}>
                  <Button
                    size="sm" flex={1}
                    variant={form.fixo ? 'solid' : 'outline'}
                    colorScheme={form.fixo ? 'blue' : 'gray'}
                    onClick={() => setForm({ ...form, fixo: true })}
                    leftIcon={<Building2 size={14} />}
                  >
                    Fixo (mensal)
                  </Button>
                  <Button
                    size="sm" flex={1}
                    variant={!form.fixo ? 'solid' : 'outline'}
                    colorScheme={!form.fixo ? 'orange' : 'gray'}
                    onClick={() => setForm({ ...form, fixo: false })}
                    leftIcon={<ShoppingCart size={14} />}
                  >
                    Variável (pontual)
                  </Button>
                </Flex>
              </FormControl>

              {/* Descrição e Valor lado a lado */}
              <SimpleGrid columns={2} spacing={3} w="100%">
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Descrição</FormLabel>
                  <Input
                    size="sm" placeholder="Ex: Aluguel, Rendimento..."
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    maxLength={255}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Valor (R$)</FormLabel>
                  <Input
                    size="sm" type="number" step="0.01" min="0"
                    placeholder="0,00"
                    value={form.valor}
                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                  />
                </FormControl>
              </SimpleGrid>

              {/* Data (só variável) */}
              {!form.fixo && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Data do Gasto</FormLabel>
                  <Input
                    size="sm" type="date"
                    value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                  />
                </FormControl>
              )}

              {/* Vigência (só fixo) */}
              {form.fixo && (
                <Box w="100%" borderTop="1px solid" borderColor="gray.100" pt={4}>
                  <Flex align="center" gap={2} mb={3}>
                    <Icon as={Calendar} boxSize={4} color="gray.500" />
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Vigência (opcional)
                    </Text>
                  </Flex>
                  <SimpleGrid columns={2} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.500">Início da vigência</FormLabel>
                      <Input
                        size="sm" type="date"
                        value={form.dataInicio}
                        onChange={(e) => setForm({ ...form, dataInicio: e.target.value })}
                        placeholder="Vigente desde sempre"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.500">Fim da vigência</FormLabel>
                      <Input
                        size="sm" type="date"
                        value={form.dataFim}
                        onChange={(e) => setForm({ ...form, dataFim: e.target.value })}
                        min={form.dataInicio || undefined}
                        placeholder="Vigente indefinidamente"
                      />
                    </FormControl>
                  </SimpleGrid>
                  <Text fontSize="2xs" color="gray.400" mt={1}>
                    Deixe em branco para vigência permanente. Use para custos que mudam ao longo do tempo (ex: aluguel).
                  </Text>
                </Box>
              )}

              {/* Categorização: 3 selects em cascata */}
              <Box w="100%" borderTop="1px solid" borderColor="gray.100" pt={4}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
                  Categorização
                </Text>
                <SimpleGrid columns={3} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">Grupo</FormLabel>
                    <Select
                      size="sm" placeholder="Selecione..."
                      value={form.grupoSel}
                      onChange={(e) => setForm({ ...form, grupoSel: e.target.value, categoriaSel: '', subcategoriaSel: '', dreCategoriaId: '' })}
                    >
                      {gruposDisponiveis.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">Categoria</FormLabel>
                    <Select
                      size="sm"
                      placeholder={form.grupoSel ? 'Selecione...' : 'Grupo primeiro'}
                      value={form.categoriaSel}
                      onChange={(e) => setForm({ ...form, categoriaSel: e.target.value, subcategoriaSel: '', dreCategoriaId: '' })}
                      isDisabled={!form.grupoSel}
                    >
                      {categoriasDoGrupo.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">Subcategoria</FormLabel>
                    <Select
                      size="sm"
                      placeholder={form.categoriaSel ? 'Selecione...' : 'Categoria primeiro'}
                      value={form.subcategoriaSel}
                      onChange={(e) => escolherSubcategoria(e.target.value)}
                      isDisabled={!form.categoriaSel}
                    >
                      {subcategoriasOpts.map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
                {temItens && (
                  <FormControl mt={3}>
                    <FormLabel fontSize="xs" color="gray.500">Item</FormLabel>
                    <Select
                      size="sm"
                      placeholder={folhaSemItem ? undefined : 'Selecione o item...'}
                      value={form.dreCategoriaId}
                      onChange={(e) => setForm({ ...form, dreCategoriaId: e.target.value })}
                    >
                      {folhaSemItem && (
                        <option value={String(folhaSemItem.id)}>Sem item (fica direto na subcategoria)</option>
                      )}
                      {folhasDaSubcategoria.filter(f => f.item).map(f => (
                        <option key={f.id} value={String(f.id)}>
                          {f.item}{f.tipo === 'receita' ? ' (receita)' : ''}
                        </option>
                      ))}
                    </Select>
                    <Text fontSize="2xs" color="gray.400" mt={1}>
                      Quarto nível do cadastro. Em branco, o lançamento entra direto na subcategoria.
                    </Text>
                  </FormControl>
                )}
              </Box>

              {/* Alocação por canal (ticket #2237) */}
              <Box w="100%" borderTop="1px solid" borderColor="gray.100" pt={4}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
                  Alocação por canal
                </Text>
                <Flex gap={2} mb={form.alocacao === 'atribuivel' ? 3 : 0}>
                  <Button
                    size="sm" flex={1}
                    variant={form.alocacao === 'overhead' ? 'solid' : 'outline'}
                    colorScheme={form.alocacao === 'overhead' ? 'gray' : 'gray'}
                    onClick={() => setForm({ ...form, alocacao: 'overhead', canaisSel: [] })}
                  >
                    Overhead (empresa toda)
                  </Button>
                  <Button
                    size="sm" flex={1}
                    variant={form.alocacao === 'atribuivel' ? 'solid' : 'outline'}
                    colorScheme={form.alocacao === 'atribuivel' ? 'blue' : 'gray'}
                    onClick={() => setForm({ ...form, alocacao: 'atribuivel' })}
                  >
                    Atribuível a canais
                  </Button>
                </Flex>
                {form.alocacao === '' && (
                  <Text fontSize="2xs" color="orange.500" mt={2}>
                    Lançamento ainda não classificado: comporta-se como overhead (só aparece no DRE consolidado).
                  </Text>
                )}
                {form.alocacao === 'overhead' && (
                  <Text fontSize="2xs" color="gray.400" mt={2}>
                    Despesa da empresa como um todo. Não aparece quando o DRE é filtrado por canal.
                  </Text>
                )}
                {form.alocacao === 'atribuivel' && (
                  <>
                    <Wrap spacing={3}>
                      {canaisDisponiveis.map(canal => (
                        <WrapItem key={canal.id}>
                          <Checkbox
                            size="sm"
                            isChecked={form.canaisSel.includes(canal.id)}
                            onChange={(e) => {
                              const canaisSel = e.target.checked
                                ? [...form.canaisSel, canal.id]
                                : form.canaisSel.filter(id => id !== canal.id);
                              setForm({ ...form, canaisSel });
                            }}
                          >
                            <Text fontSize="sm">{canal.nome}</Text>
                          </Checkbox>
                        </WrapItem>
                      ))}
                    </Wrap>
                    <Text fontSize="2xs" color="gray.400" mt={2}>
                      Um canal: 100% do valor nele. Vários canais: rateio proporcional à receita de cada um no período.
                    </Text>
                  </>
                )}
              </Box>

              {/* Alertas informativos */}
              {tipoCategoriaSelecionada === 'receita' && (
                <Alert status="success" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Este lançamento é uma <b>receita</b> — será somado ao lucro no DRE.
                </Alert>
              )}
              {tipoCategoriaSelecionada === 'despesa' && (
                <Alert status="warning" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Este lançamento é uma <b>despesa</b> — será subtraído do lucro no DRE.
                </Alert>
              )}
              {form.fixo && (
                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Lançamentos fixos são recorrentes e prorateados automaticamente no DRE por período.
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
            <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={saving}>
              {editId ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Importação em Massa (#2238) */}
      <Modal isOpen={importDisc.isOpen} onClose={importDisc.onClose} size={impModalSize} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" pb={2}>
            <Flex align="center" gap={2}>
              <FileSpreadsheet size={18} />
              <Text>Importar despesas em massa</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {impEtapa === 'inicio' && (
              <VStack spacing={4} align="stretch">
                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Baixe o template, preencha offline e envie de volta. Somente .xlsx.
                  As listas de Categoria e Canal do template refletem o cadastro atual.
                </Alert>
                <Button size="sm" variant="outline" colorScheme="blue" leftIcon={<Download size={14} />}
                  onClick={baixarTemplate} isLoading={impBaixando}>
                  Baixar template (.xlsx)
                </Button>
                <FormControl>
                  <FormLabel fontSize="sm">Arquivo preenchido</FormLabel>
                  <Input ref={importFileRef} type="file" size="sm" pt={1}
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
                </FormControl>
                <Alert status="warning" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Se alguma linha estiver com erro ou duplicada, nada é importado. Corrija a planilha e envie de novo.
                </Alert>
              </VStack>
            )}

            {impEtapa === 'erros' && (
              <VStack spacing={3} align="stretch">
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {impErros.length} erro(s) encontrado(s){impArquivoNome ? ` em ${impArquivoNome}` : ''}. Nada foi importado.
                </Alert>
                <TableContainer maxH="50vh" overflowY="auto">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr bg="red.50">
                        <Th fontSize="xs" w="80px">Linha</Th>
                        <Th fontSize="xs" w="180px">Coluna</Th>
                        <Th fontSize="xs">Motivo</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {impErros.map((e, i) => (
                        <Tr key={i}>
                          <Td fontSize="sm" fontWeight="bold">{e.linha}</Td>
                          <Td fontSize="sm">{e.coluna}</Td>
                          <Td fontSize="sm" color="red.600">{e.motivo}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </VStack>
            )}

            {impEtapa === 'preview' && (
              <VStack spacing={3} align="stretch">
                <Alert status="success" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Arquivo validado. Confira as {impDespesas.length} despesas abaixo: você pode editar ou excluir antes de confirmar.
                </Alert>
                <TableContainer maxH="55vh" overflowY="auto">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr bg="gray.50">
                        <Th fontSize="xs" w="50px">Linha</Th>
                        <Th fontSize="xs" minW="200px">Descrição</Th>
                        <Th fontSize="xs" w="120px">Tipo</Th>
                        <Th fontSize="xs" w="110px">Valor</Th>
                        <Th fontSize="xs" w="150px">Data / Vigência</Th>
                        <Th fontSize="xs" minW="240px">Categoria</Th>
                        <Th fontSize="xs" minW="180px">Canal</Th>
                        <Th fontSize="xs" w="50px"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {impDespesas.map((d, idx) => (
                        <Tr key={idx}>
                          <Td fontSize="xs" color="gray.500">{d.linha}</Td>
                          <Td>
                            <Input size="xs" value={d.descricao}
                              onChange={(e) => editarDespesaImp(idx, 'descricao', e.target.value)} />
                          </Td>
                          <Td>
                            <Select size="xs" value={d.fixo ? '1' : '0'}
                              onChange={(e) => editarDespesaImp(idx, 'fixo', e.target.value === '1')}>
                              <option value="0">Pontual</option>
                              <option value="1">Recorrente</option>
                            </Select>
                          </Td>
                          <Td>
                            <Input size="xs" type="number" step="0.01" min="0" value={d.valor}
                              onChange={(e) => editarDespesaImp(idx, 'valor', e.target.value)} />
                          </Td>
                          <Td>
                            {d.fixo ? (
                              <VStack spacing={1}>
                                <Input size="xs" type="date" value={d.data_inicio || ''}
                                  onChange={(e) => editarDespesaImp(idx, 'data_inicio', e.target.value || null)} />
                                <Input size="xs" type="date" value={d.data_fim || ''}
                                  onChange={(e) => editarDespesaImp(idx, 'data_fim', e.target.value || null)} />
                              </VStack>
                            ) : (
                              <Input size="xs" type="date" value={d.data || ''}
                                onChange={(e) => editarDespesaImp(idx, 'data', e.target.value || null)} />
                            )}
                          </Td>
                          <Td>
                            <Select size="xs" value={d.categoria_caminho}
                              onChange={(e) => editarDespesaImp(idx, 'categoria_caminho', e.target.value)}>
                              {categoriaLista.map(c => (
                                <option key={c.id} value={c.label}>{c.label}</option>
                              ))}
                            </Select>
                          </Td>
                          <Td>
                            <Select size="xs" value={d.canal_combo}
                              onChange={(e) => editarDespesaImp(idx, 'canal_combo', e.target.value)}>
                              {combosCanal.map(label => (
                                <option key={label} value={label}>{label}</option>
                              ))}
                            </Select>
                          </Td>
                          <Td>
                            <IconButton icon={<Trash2 size={13} />} size="xs" variant="ghost" colorScheme="red"
                              aria-label="Remover" onClick={() => removerDespesaImp(idx)} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
                <Flex justify="space-between" fontSize="sm" fontWeight="semibold" px={1}>
                  <Text>{impDespesas.length} despesa(s)</Text>
                  <Text>Soma: {formatMoney(somaImportacao)}</Text>
                </Flex>
                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  Ao confirmar, tudo é validado de novo (inclusive duplicidade) e gravado de uma vez. Nenhuma linha é importada se houver erro.
                </Alert>
              </VStack>
            )}

            {impEtapa === 'concluido' && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                {impResumo?.message || 'Despesas importadas com sucesso.'}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            {impEtapa === 'inicio' && (
              <>
                <Button size="sm" variant="ghost" mr={3} onClick={importDisc.onClose}>Cancelar</Button>
                <Button size="sm" colorScheme="green" leftIcon={<Upload size={14} />}
                  onClick={validarArquivo} isLoading={impProcessando}>
                  Validar arquivo
                </Button>
              </>
            )}
            {impEtapa === 'erros' && (
              <>
                <Button size="sm" variant="ghost" mr={3} onClick={importDisc.onClose}>Fechar</Button>
                {impDespesas.length > 0 && (
                  <Button size="sm" variant="outline" mr={3} onClick={() => setImpEtapa('preview')}>
                    Voltar à conferência
                  </Button>
                )}
                <Button size="sm" colorScheme="blue" onClick={() => setImpEtapa('inicio')}>
                  Enviar outro arquivo
                </Button>
              </>
            )}
            {impEtapa === 'preview' && (
              <>
                <Button size="sm" variant="ghost" mr={3} onClick={importDisc.onClose}>Cancelar</Button>
                <Button size="sm" colorScheme="green" onClick={confirmarImportacao} isLoading={impProcessando}>
                  Confirmar importação ({impDespesas.length})
                </Button>
              </>
            )}
            {impEtapa === 'concluido' && (
              <Button size="sm" colorScheme="blue" onClick={importDisc.onClose}>Fechar</Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={iaDisc.isOpen} onClose={iaDisc.onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" pb={2}>
            <Text>Reclassificar origem de IA</Text>
            {iaEditando && (
              <Text fontSize="xs" color="gray.500" fontFamily="mono" fontWeight="normal">
                {iaEditando.origem}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Rótulo contábil</FormLabel>
                <Input
                  size="sm"
                  placeholder="Ex: Resumo de ligação"
                  value={iaForm.rotulo}
                  onChange={(e) => setIaForm({ ...iaForm, rotulo: e.target.value })}
                />
                <Text fontSize="2xs" color="gray.500" mt={1}>
                  Nome que aparece no DRE. O casamento com o gasto continua pelo identificador técnico.
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Classificação contábil</FormLabel>
                <Select
                  size="sm"
                  placeholder="Selecione..."
                  value={iaForm.dre_categoria_custo_id}
                  onChange={(e) => setIaForm({ ...iaForm, dre_categoria_custo_id: e.target.value })}
                >
                  {(categorias.lista || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Alocação</FormLabel>
                <Select
                  size="sm"
                  value={iaForm.alocacao}
                  onChange={(e) => setIaForm({ ...iaForm, alocacao: e.target.value })}
                >
                  <option value="atribuivel">Atribuível (rateia entre os canais)</option>
                  <option value="overhead">Overhead (empresa toda)</option>
                </Select>
              </FormControl>

              {iaForm.alocacao === 'atribuivel' && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Canais</FormLabel>
                  <VStack align="start" spacing={1}>
                    {canaisDisponiveis.map((canal) => (
                      <Checkbox
                        key={canal.id}
                        size="sm"
                        isChecked={iaForm.canais.includes(canal.id)}
                        onChange={() => toggleCanalIa(canal.id)}
                      >
                        {canal.nome}
                      </Checkbox>
                    ))}
                  </VStack>
                  <Text fontSize="2xs" color="gray.500" mt={1}>
                    Com mais de um canal, o valor é rateado proporcionalmente à receita de cada um
                    no período.
                  </Text>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button size="sm" variant="ghost" onClick={iaDisc.onClose}>Cancelar</Button>
              <Button size="sm" colorScheme="blue" onClick={handleSaveIa} isLoading={iaSaving}>
                Salvar
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DRECustos;
